import { BaseController } from '../../../../base_controller';
import zoomSdk, { Apis } from '@zoom/appssdk';

type InternalMessage = {
  id: string;
  data?: object;
};

export const MESSAGES = {
  FOCUS_APP: 'focus_app',
  UNFOCUS_APP: 'unfocus_app',
};

export default class extends BaseController {
  static targets = ['errorMessage'];

  declare errorMessageTarget: HTMLElement;

  static values = {
    userHost: Boolean,
    inMeeting: Boolean,
  };

  declare userHostValue: boolean;
  declare inMeetingValue: boolean;

  async connect(): Promise<void> {
    await this.initializeZoom();
    this.setupListeners();
  }

  // private methods

  private async initializeZoom(): Promise<void> {
    try {
      const configResponse = await zoomSdk.config({
        capabilities: this.guestCapabilites(),
      });

      this.logger('Zoom JS SDK Configuration', configResponse);
    } catch (e) {
      console.error(e);
      this.showErrorMessage('There was an error configuring the Zoom SDK');
    }
  }

  private guestCapabilites(): Apis[] {
    return [
      'shareApp',
      'onMessage',
      'expandApp',
      'bringAppToFront',
      'sendAppToBackground',
    ];
  }

  private async focusApp(): Promise<void> {
    await zoomSdk.bringAppToFront();
    await zoomSdk.expandApp({ action: 'expand' });
  }

  private async unfocusApp(): Promise<void> {
    await zoomSdk.sendAppToBackground();
  }

  private async handleMessage(message: InternalMessage): Promise<void> {
    const dict = {
      [MESSAGES.FOCUS_APP]: 'focusApp',
      [MESSAGES.UNFOCUS_APP]: 'unfocusApp',
    };

    const functionName = dict[message.id];

    try {
      this.hideErrorMessage();
      await this[functionName]();
    } catch (e) {
      console.error(e);
      this.showErrorMessage('There was an error to process the message');
    }
  }

  private setupListeners(): void {
    zoomSdk.onMessage(({ timestamp, payload }) => {
      this.logger(`(${timestamp}) New message received:`, payload);

      this.handleMessage(payload as InternalMessage);
    });
  }

  protected logger(message, details): void {
    const finalMessage = `[ZoomIntegration] ${message}`;

    super.logger(finalMessage, details, '#b526b0');
  }

  private showErrorMessage(message) {
    this.errorMessageTarget.classList.remove('hide');
    this.errorMessageTarget.textContent = message;
  }

  private hideErrorMessage() {
    this.errorMessageTarget.classList.add('hide');
  }
}
